import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import {
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  Spinner,
  timeAgo,
} from '../components/common'
import { DataTable, type Column } from '../components/table'
import type { CaptureProbe, FlowExporter } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Chi sta mandando i flussi, e con quale campionamento.
 *
 * Sta in una pagina propria e non in coda alle conversazioni perché risponde a
 * una domanda diversa: non «cosa succede nella rete» ma «la raccolta sta
 * funzionando». Ci si arriva quando i numeri sembrano sbagliati, ed è il primo
 * posto da guardare.
 */
export default function FlowExporters() {
  const exporters = useAsync(() => api.flowExporters(), [])
  const probes = useAsync(() => api.flowCaptureProbes(), [])

  // Le sonde che catturano dal filo stanno nello stesso elenco, con protocollo
  // «Sonda»: sono una sorgente di flussi come un router che esporta, e in
  // un'altra sezione in fondo alla pagina non le trovava nessuno.
  const righe: RigaSorgente[] = [
    ...(exporters.data ?? []),
    ...(probes.data ?? []).map(daSonda),
  ]
  // L'avviso in cima parla degli apparati che esportano; lo stato di una sonda
  // in cattura sta sulla sua riga.
  const silent = (exporters.data ?? []).filter((e) => e.silent).length
  const sampled = righe.filter((e) => e.sampling > 1).length

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Esportatori')}</h1>
          <p className="page__subtitle">{t('Apparati che inviano flussi alle sonde')}</p>
        </div>
      </header>

      <ErrorBanner error={exporters.error} />

      {/* Un apparato che tace è un **buco nei dati**, non assenza di traffico: i
          grafici scendono e la rete non è cambiata. Va detto in cima, non trovato
          scorrendo una colonna. */}
      {silent > 0 && (
        <div className="banner banner--warn">
          {silent === 1
            ? t('Un apparato non esporta da oltre un’ora. Il traffico che passava di lì non compare più nei grafici: è un buco nei dati, non una rete diventata silenziosa.')
            : t('{n} apparati non esportano da oltre un’ora. Il traffico che passava di lì non compare più nei grafici: è un buco nei dati, non una rete diventata silenziosa.', { n: silent })}
        </div>
      )}

      <Card title={t('Sorgenti di flussi ({n})', { n: righe.length })}>
        {exporters.loading && !exporters.data ? (
          <Spinner />
        ) : !righe.length ? (
          <EmptyState
            title={t('Nessun exporter')}
            hint={t('Gli apparati inviano i flussi alla sonda: nessuno li ha ancora inviati.')}
          />
        ) : (
          <DataTable
            rows={righe}
            columns={colonne(() => void exporters.reload())}
            rowKey={(e) => (e.sonda ? `sonda:${e.sonda.appliance_id}` : e.address)}
            initialSort={{ key: 'flows_total', desc: true }}
            sourceNote={t("L'elenco è completo: gli apparati che hanno esportato almeno un flusso e le sonde che catturano dal filo (protocollo «Sonda»).")}
          />
        )}

        {sampled > 0 && (
          <p className="field__hint">
            {sampled === 1
              ? t('Un apparato esporta un campione del traffico. I volumi sono riportati in scala, quindi restano confrontabili — ma sono stime, e su volumi bassi la stima è grossolana.')
              : t('{n} apparati esportano un campione del traffico. I volumi sono riportati in scala, quindi restano confrontabili — ma sono stime, e su volumi bassi la stima è grossolana.', { n: sampled })}
          </p>
        )}

        {exporters.data?.some((e) => e.first_seen) && (
          <p className="field__hint">
            {t('Primo flusso ricevuto il {data}.', {
              data: formatDateTime(exporters.data.map((e) => e.first_seen).sort()[0] ?? null),
            })}
          </p>
        )}
      </Card>

    </>
  )
}

/** Una riga dell'elenco: un esportatore, o una sonda che cattura dal filo. */
type RigaSorgente = FlowExporter & { sonda?: CaptureProbe }

function daSonda(p: CaptureProbe): RigaSorgente {
  return {
    address: p.name,
    version: 'Sonda',
    appliance_id: p.appliance_id,
    first_seen: null,
    last_seen: p.last_seen,
    flows_total: p.flows,
    sampling: p.sampling,
    sample_every: 1,
    silent: p.silent || !p.running,
    sonda: p,
  }
}

function colonne(onCambiato: () => void): Column<RigaSorgente>[] {
  return [
  {
    key: 'address',
    header: t('Indirizzo'),
    value: (e) => e.address,
    render: (e) =>
      e.sonda ? (
        <span>
          <Link to={`/fleet/${e.sonda.appliance_id}`}>{e.sonda.name}</Link>{' '}
          <span className="muted mono" title={t('Interfaccia in cattura')}>{e.sonda.interface}</span>
        </span>
      ) : (
        <span className="mono">{e.address}</span>
      ),
  },
  {
    key: 'version', header: t('Protocollo'),
    value: (e) => (e.sonda ? t('Sonda') : e.version ?? '—'),
    render: (e) =>
      e.sonda ? (
        <span className="chip" title={t('Flussi ricavati dalla sonda osservando il traffico su una porta mirror, non ricevuti da un apparato.')}>
          {t('Sonda')}
        </span>
      ) : (
        e.version ?? '—'
      ),
  },
  {
    key: 'flows_total',
    header: t('Flussi ricevuti'),
    type: 'number',
    align: 'right',
    value: (e) => e.flows_total,
    render: (e) => e.flows_total.toLocaleString(localeCorrente()),
  },
  {
    key: 'sampling',
    header: t('Campionamento'),
    type: 'number',
    align: 'right',
    value: (e) => e.sampling,
    // Un volume ricavato da un campione non è un volume misurato. Dirlo qui è
    // l'unico punto in cui chi legge un grafico può sapere quanto fidarsi dei
    // numeri che ci stanno sopra.
    render: (e) =>
      e.sampling > 1 ? (
        <span title={t('I volumi sono riportati in scala: sono stime.')}>
          {t('1 su {n}', { n: e.sampling.toLocaleString(localeCorrente()) })}
        </span>
      ) : (
        t('ogni flusso')
      ),
  },
  {
    key: 'stato',
    header: t('Stato'),
    value: (e) => (e.silent ? t('fermo') : t('attivo')),
    render: (e) =>
      e.sonda ? (
        <span>
          {!e.sonda.running ? (
            <span className="chip" title={e.sonda.error ?? ''}>{e.sonda.error ? t('non partita') : t('in attesa')}</span>
          ) : e.silent ? (
            <span className="chip">{t("non si sente da oltre un'ora")}</span>
          ) : (
            <span className="chip chip--ok">{t('in cattura')}</span>
          )}
          {e.sonda.dropped > 0 && (
            <span className="badge badge--warn" title={t('Pacchetti buttati dal kernel perché la sonda non teneva il passo: i volumi sono incompleti.')}>
              {' '}{t('{n} scartati', { n: e.sonda.dropped.toLocaleString(localeCorrente()) })}
            </span>
          )}
        </span>
      ) : e.silent ? (
        <span className="chip">{t("non esporta da oltre un'ora")}</span>
      ) : (
        <span className="chip chip--ok">{t('attivo')}</span>
      ),
  },
  {
    key: 'last_seen',
    header: t('Ultimo invio'),
    type: 'date',
    value: (e) => e.last_seen,
    render: (e) => <span title={e.last_seen ?? ''}>{timeAgo(e.last_seen)}</span>,
  },
  {
    // Il campionamento **deciso qui**, per alleggerire l'ingest: la sonda terrà
    // 1 flusso ogni N da questo apparato, scalandone i volumi. Diverso dalla
    // colonna «Campionamento», che è ciò che l'apparato dichiara.
    key: 'sample_every',
    header: t('Alleggerisci ingest'),
    value: (e) => e.sample_every,
    searchable: false,
    render: (e) => (e.sonda ? <span className="muted">—</span> : <CampionamentoCell exporter={e} onCambiato={onCambiato} />),
  },
  ]
}

/** Il menu del campionamento operatore per un esportatore: «tieni tutto» o 1
 *  ogni 2/10/30/100. Salva subito e ricarica; se il server rifiuta, torna al
 *  valore di prima invece di mostrarne uno che non è stato applicato. */
function CampionamentoCell({ exporter, onCambiato }: {
  exporter: FlowExporter
  onCambiato: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState(false)

  async function cambia(n: number) {
    setBusy(true)
    setErrore(false)
    try {
      await api.setExporterSampling(exporter.address, n)
      onCambiato()
    } catch {
      setErrore(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <span className="toolbar" style={{ gap: 6, justifyContent: 'flex-end' }}>
      <select
        value={exporter.sample_every}
        disabled={busy}
        aria-label={t('Campionamento di {ind}', { ind: exporter.address })}
        onChange={(ev) => void cambia(Number(ev.target.value))}
      >
        <option value={1}>{t('tieni tutto')}</option>
        <option value={2}>{t('1 ogni 2')}</option>
        <option value={10}>{t('1 ogni 10')}</option>
        <option value={30}>{t('1 ogni 30')}</option>
        <option value={100}>{t('1 ogni 100')}</option>
      </select>
      {errore && <span className="badge badge--error" title={t('Non salvato')}>!</span>}
    </span>
  )
}
